import type { RcuConfig } from '../lib/rcu/model';
import { asDaliBridgeId, asLoadId, asRailId, asRoomId } from '../lib/rcu/ids';
import { CHANNEL_KIND } from '../lib/rcu/catalog/channel';
import { LOAD_ICON } from '../lib/rcu/catalog/icon';
import { LOAD_TYPE } from '../lib/rcu/catalog/loadType';
import { RAIL_TYPE } from '../lib/rcu/catalog/railType';
import { SCHEMA_VERSION } from '../lib/rcu/catalog/storage';

/**
 * A ready-made layout for demonstrations and manual testing: one RCU with
 * onboard outputs, four rail types, a DALI bridge, two rooms and a spread of
 * loads — including one that is deliberately left unwired.
 *
 * Written by hand rather than through commands so the ids are stable, which
 * makes it usable as a fixture too.
 */
const bedroom = asRoomId('room_demo_bedroom');
const bathroom = asRoomId('room_demo_bathroom');

const phaseCutRail = asRailId('rail_demo_phase_cut_0');
const relayRail = asRailId('rail_demo_relay_0');
const curtainRail = asRailId('rail_demo_curtain_0');
const fanRail = asRailId('rail_demo_fan_0');
const daliBridge = asDaliBridgeId('bridge_demo_dali_0');

const din = (railId: typeof phaseCutRail, channel: number) =>
  ({ kind: CHANNEL_KIND.DIN, railId, channel }) as const;
const rcu = (channel: number) => ({ kind: CHANNEL_KIND.RCU, channel }) as const;

export const SAMPLE_CONFIG_NOW = '2024-05-01T09:00:00.000Z';

export const sampleRcuConfig = (): RcuConfig => ({
  schemaVersion: SCHEMA_VERSION,
  rcu: {
    name: 'Room 101 controller',
    deviceId: 'TYM-RCU-0001',
    serialNumber: '888854',
    ipAddress: '10.0.0.24',
    location: 'Level 3 riser',
    outputCount: 12,
  },
  rails: [
    { id: phaseCutRail, type: RAIL_TYPE.PHASE_CUT, address: 0, label: 'Bedroom dimming' },
    { id: relayRail, type: RAIL_TYPE.RELAY, address: 0, label: 'Bathroom switching' },
    { id: curtainRail, type: RAIL_TYPE.CURTAIN, address: 0, label: 'Bedroom curtains' },
    { id: fanRail, type: RAIL_TYPE.FAN, address: 0, label: 'Bedroom fan' },
  ],
  daliBridges: [
    { id: daliBridge, address: 0, master: 0, label: 'Bedroom DALI' },
  ],
  rooms: [
    { id: bedroom, name: 'Bedroom', note: 'Corner suite', createdAt: SAMPLE_CONFIG_NOW },
    { id: bathroom, name: 'Bathroom', note: '', createdAt: SAMPLE_CONFIG_NOW },
  ],
  loads: [
    {
      id: asLoadId('load_demo_ceiling'),
      name: 'Ceiling downlights',
      type: LOAD_TYPE.PHASE_CUT,
      roomId: bedroom,
      channel: din(phaseCutRail, 1),
      wattage: 48,
      iconKey: LOAD_ICON.CEILING,
      createdAt: SAMPLE_CONFIG_NOW,
    },
    {
      id: asLoadId('load_demo_bedside'),
      name: 'Bedside reading lights',
      type: LOAD_TYPE.PHASE_CUT,
      roomId: bedroom,
      channel: din(phaseCutRail, 2),
      wattage: 12,
      iconKey: LOAD_ICON.WALL_LIGHT,
      createdAt: SAMPLE_CONFIG_NOW,
    },
    {
      id: asLoadId('load_demo_cove'),
      name: 'Cove strip',
      type: LOAD_TYPE.PHASE_CUT,
      roomId: bedroom,
      channel: din(phaseCutRail, 3),
      wattage: 36,
      iconKey: LOAD_ICON.STRIP,
      createdAt: SAMPLE_CONFIG_NOW,
    },
    {
      id: asLoadId('load_demo_curtains'),
      name: 'Blackout curtains',
      type: LOAD_TYPE.CURTAIN,
      roomId: bedroom,
      channel: din(curtainRail, 1),
      wattage: null,
      iconKey: LOAD_ICON.BLIND,
      createdAt: SAMPLE_CONFIG_NOW,
    },
    {
      id: asLoadId('load_demo_bathroom'),
      name: 'Bathroom downlights',
      type: LOAD_TYPE.RELAY,
      roomId: bathroom,
      channel: din(relayRail, 1),
      wattage: 24,
      iconKey: LOAD_ICON.SPOT,
      createdAt: SAMPLE_CONFIG_NOW,
    },
    {
      id: asLoadId('load_demo_extractor'),
      name: 'Extractor fan',
      type: LOAD_TYPE.RELAY,
      roomId: bathroom,
      channel: din(relayRail, 2),
      wattage: 30,
      iconKey: LOAD_ICON.OUTLET,
      createdAt: SAMPLE_CONFIG_NOW,
    },
    {
      id: asLoadId('load_demo_wardrobe'),
      name: 'Wardrobe light',
      type: LOAD_TYPE.RELAY,
      roomId: bedroom,
      channel: rcu(1),
      wattage: 8,
      iconKey: LOAD_ICON.TABLE_LAMP,
      createdAt: SAMPLE_CONFIG_NOW,
    },
    {
      id: asLoadId('load_demo_balcony'),
      name: 'Balcony light',
      type: LOAD_TYPE.RELAY,
      roomId: null,
      channel: rcu(2),
      wattage: 18,
      iconKey: LOAD_ICON.FLOOR_LAMP,
      createdAt: SAMPLE_CONFIG_NOW,
    },
    {
      id: asLoadId('load_demo_spare'),
      name: 'Spare outlet',
      type: LOAD_TYPE.RELAY,
      roomId: null,
      channel: null,
      wattage: null,
      iconKey: LOAD_ICON.OUTLET,
      createdAt: SAMPLE_CONFIG_NOW,
    },
  ],
  defaultRoomId: bedroom,
  updatedAt: SAMPLE_CONFIG_NOW,
});
